'use client';

import { createContext, use } from 'react';
import { tv } from 'tailwind-variants';

import { label } from '../typography/Label';

import type { VariantProps } from 'tailwind-variants';

type CardPadding = 'sm' | 'md' | 'lg';

const CardPaddingContext = createContext<CardPadding>('lg');

// CARD
const card = tv({
  base: `
    bg-background-primary
    text-card-foreground
    flex-wrap-nowrap
    flex
    shrink-0
    flex-col
    gap-4
    rounded-lg
    border
    group/card
  `,
  variants: {
    variant: {
      default: `
        border-border-secondary
      `,
      negative: `
        bg-background-negative-light
        border-border-tag-negative
        text-content-negative-dark
      `,
    },
    padding: {
      sm: 'py-4',
      md: 'py-5',
      lg: 'py-6',
    },
    hoverable: {
      true: 'hover:bg-background-secondary',
    },
  },
  defaultVariants: {
    hoverable: false,
    padding: 'md',
    variant: 'default',
  },
});
function Card({
  children,
  className,
  hoverable,
  padding = 'lg',
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof card>): React.ReactElement {
  return (
    <CardPaddingContext value={padding}>
      <div className={card({ variant, padding, hoverable, className })} data-slot="card" {...props}>
        {children}
      </div>
    </CardPaddingContext>
  );
}

// CARD HEADER
const cardHeader = tv({
  base: `
    @container/card-header
    grid
    auto-rows-min
    grid-rows-[auto_auto]
    items-start
    gap-1.5
    has-data-[slot=card-action]:grid-cols-[1fr_auto]
  `,
  variants: {
    bordered: {
      true: `
        border-b
        pb-2
      `,
    },
    padding: {
      sm: 'px-4',
      md: 'px-5',
      lg: 'px-6',
    },
  },
  defaultVariants: {
    padding: 'lg',
  },
});

function CardHeader({
  bordered,
  className,
  ...props
}: React.ComponentProps<'div'> & Omit<VariantProps<typeof cardHeader>, 'padding'>): React.ReactElement {
  const padding = use(CardPaddingContext);
  return <div className={cardHeader({ bordered, padding, className })} data-slot="card-header" {...props} />;
}

// CARD TITLE
const cardTitle = tv({
  extend: label,
  defaultVariants: {
    size: 'lg',
    weight: 'medium',
  },
});

function CardTitle({
  className,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof cardTitle> & VariantProps<typeof label>): React.ReactElement {
  return <div className={cardTitle({ className, ...props })} data-slot="card-title" {...props} />;
}

// CARD DESCRIPTION
const cardDescription = tv({
  extend: label,
  base: `
    text-secondary
  `,
  defaultVariants: {
    size: 'md',
  },
});
function CardDescription({ className, ...props }: React.ComponentProps<'div'>): React.ReactElement {
  return <div className={cardDescription({ className })} data-slot="card-description" {...props} />;
}

// CARD ACTION
const cardAction = tv({
  base: `
    col-start-2
    row-span-2
    row-start-1
    self-start
    justify-self-end
  `,
});
function CardAction({ className, ...props }: React.ComponentProps<'div'>): React.ReactElement {
  return <div className={cardAction({ className })} data-slot="card-action" {...props} />;
}

// CARD CONTENT
const cardContent = tv({
  base: '',
  variants: {
    padding: {
      sm: 'px-4',
      md: 'px-5',
      lg: 'px-6',
    },
  },
  defaultVariants: {
    padding: 'lg',
  },
});
function CardContent({ className, ...props }: React.ComponentProps<'div'>): React.ReactElement {
  const padding = use(CardPaddingContext);
  return <div className={cardContent({ padding, className })} data-slot="card-content" {...props} />;
}

// CARD FOOTER
const cardFooter = tv({
  base: `
    flex
    items-center
  `,
  variants: {
    bordered: {
      true: `
        border-t pt-6
      `,
    },
    padding: {
      sm: 'px-4',
      md: 'px-5',
      lg: 'px-6',
    },
  },
  defaultVariants: {
    padding: 'lg',
  },
});
function CardFooter({
  bordered,
  className,
  ...props
}: React.ComponentProps<'div'> & Omit<VariantProps<typeof cardFooter>, 'padding'>): React.ReactElement {
  const padding = use(CardPaddingContext);
  return <div className={cardFooter({ bordered, padding, className })} data-slot="card-footer" {...props} />;
}

export {
  card,
  Card,
  cardAction,
  CardAction,
  cardContent,
  CardContent,
  cardDescription,
  CardDescription,
  cardFooter,
  CardFooter,
  cardHeader,
  CardHeader,
  cardTitle,
  CardTitle,
};

export type { CardPadding };
